import type { ChangeEvent } from "react";
import { useCallback } from "react";
import { twJoin } from "tailwind-merge";

import { selectionControlBoxClassName, SelectionControlGlyph } from "./SelectionControl";
import { Tooltip } from "./Tooltip";

/**
 * Props for a labelled checkbox. `indeterminate` controls the mixed visual state, `checked` applies otherwise, and
 * `ref` receives the underlying input element. `description` adds caption text under the label and top-aligns the
 * control; it requires a `label`.
 */
export type CheckboxProps = {
  id?: string;
  name?: string;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  shape?: "rounded" | "square";
  disabled?: boolean;
  tooltip?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  ref?: React.Ref<HTMLInputElement>;
} & (
  | {
      label?: never;
      description?: never;
      alignTop?: never;
    }
  | {
      label: string;
      description?: string;
      alignTop?: boolean;
    }
);
/** Renders the dashboard checkbox while forwarding its input ref and mixed-state behavior. */
export function Checkbox({
  onChange,
  shape = "square",
  ref,
  id,
  name,
  checked,
  indeterminate,
  disabled,
  label,
  description,
  tooltip,
  alignTop,
  "data-testid": dataTestid,
  ...props
}: CheckboxProps): React.ReactNode {
  const topAligned = alignTop || description !== undefined;

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) {
        node.indeterminate = indeterminate ?? false;
      }

      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [indeterminate, ref],
  );

  const control = (
    <div
      className={twJoin(
        "relative inline-flex size-selection-control shrink-0",
        topAligned ? "mt-selection-control-align" : undefined,
      )}
    >
      <input
        data-testid={dataTestid}
        {...props}
        id={id || name}
        name={name}
        ref={setInputRef}
        className="peer absolute z-10 size-full opacity-0 disabled:pointer-events-none"
        onChange={onChange}
        readOnly={!onChange}
        type="checkbox"
        disabled={disabled}
        checked={checked || false}
      />
      <div
        className={twJoin(
          selectionControlBoxClassName,
          "peer-indeterminate:border-selection-selected peer-indeterminate:bg-selection-selected peer-indeterminate-all-child:block",
          shape === "rounded" ? "rounded-full" : "rounded-sm",
          disabled && "cursor-default",
        )}
      >
        <SelectionControlGlyph variant={indeterminate ? "minus" : "check"} />
      </div>
    </div>
  );

  if (!label) {
    return <Tooltip tooltip={tooltip}>{control}</Tooltip>;
  }

  return (
    <Tooltip tooltip={tooltip}>
      <label className={twJoin("inline-flex w-fit gap-x-space-sm", topAligned ? "items-start" : "items-center")}>
        <span className="order-1 flex flex-col gap-space-xs">
          <span className={twJoin(disabled && "text-text-disabled")}>{label}</span>
          {description !== undefined && (
            <span className={twJoin("text-caption", disabled ? "text-text-disabled" : "text-text-secondary")}>
              {description}
            </span>
          )}
        </span>
        {control}
      </label>
    </Tooltip>
  );
}

type CheckboxGroupProps = {
  label: string;
  children: React.ReactNode;
};

export function CheckboxGroup({ label, children }: CheckboxGroupProps): React.ReactNode {
  return (
    <fieldset className="@container">
      <div className="flex w-full flex-col items-start justify-between gap-x-space-lg gap-y-space-sm @xs:flex-row">
        <legend className="text-body whitespace-pre-wrap">{label}</legend>
        <div className="flex min-w-selection-group shrink-0 items-center gap-md @md:justify-end">{children}</div>
      </div>
    </fieldset>
  );
}
